import clsx from 'clsx';
import React from 'react';
import Button from '../Button';
import AddIcon from '../../assets/icons/add-icon.svg?react';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import Input from '../Input';
import Select from '../Select';
import { InvestigationQuestionTypeEnum } from '@EHSNavigator/backend';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import { useFormik } from 'formik';
import { getInvestigationReportingAddQuestionSchema } from './schema';
import TextArea from '../TextArea';

interface InvestigationReportingAddQuestionModalProps {
  otherClasses?: string;
  handleClose: () => void;
  formHandler: any;
  selectedQuestion: any;
}

const InvestigationReportingAddQuestionModal: React.FC<
  InvestigationReportingAddQuestionModalProps
> = ({ otherClasses, handleClose, formHandler, selectedQuestion }) => {
  const investigationReportingAddQuestionModalClasses = clsx(otherClasses);
  const md = useButtonSizeSmall();

  const investigationReportingAddNewQuestionHandler = useFormik({
    initialValues: {
      questionType: selectedQuestion?.questionType || '',
      question: selectedQuestion?.question || '',
      answer: selectedQuestion?.answer || '',
      options: selectedQuestion?.options || [],
    },
    enableReinitialize: true,
    validationSchema: getInvestigationReportingAddQuestionSchema(),
    onSubmit: async (values) => {
      let updatedValues;
      if (values?.options?.length > 0) {
        updatedValues = {
          questionType: values?.questionType,
          question: values?.question,
          answer: values?.answer,
          options: values?.options,
        };
      } else {
        updatedValues = {
          questionType: values?.questionType,
          question: values?.question,
          answer: values?.answer,
        };
      }
      if (selectedQuestion) {
        formHandler?.setFieldValue(
          `otherQuestions.${selectedQuestion?.index}`,
          updatedValues,
        );
      } else {
        formHandler?.setFieldValue('otherQuestions', [
          ...(formHandler?.values?.otherQuestions || []),
          updatedValues,
        ]);
      }
      handleClose();
    },
  });

  return (
    <div
      className={investigationReportingAddQuestionModalClasses}
      data-testid="investigation-reporting-add-question-modal"
    >
      <div className="mb-8 flex w-full flex-col items-center gap-2.5 text-center lg:mb-12">
        {selectedQuestion ? (
          <EditIcon className="[&>path]:fill-brand-byzantine h-12 w-12 md:h-85 md:w-85" />
        ) : (
          <AddIcon className="[&>path]:fill-brand-byzantine h-12 w-12 md:h-85 md:w-85" />
        )}
        <h4 className="text-brand-byzantine">
          {selectedQuestion ? 'Edit Question' : 'Create New Question'}
        </h4>
      </div>
      <form
        action="#"
        onSubmit={investigationReportingAddNewQuestionHandler?.handleSubmit}
      >
        <Select
          list={Object.values(InvestigationQuestionTypeEnum)
            .filter(
              (item) => item !== InvestigationQuestionTypeEnum?.MultiOptions,
            )
            .map((item) => ({
              label: item,
              value: item,
            }))}
          value={
            investigationReportingAddNewQuestionHandler?.values.questionType
          }
          onSelect={(value) => {
            investigationReportingAddNewQuestionHandler?.setFieldValue(
              'questionType',
              value,
            );
            investigationReportingAddNewQuestionHandler?.setFieldValue(
              'options',
              [],
            );
            investigationReportingAddNewQuestionHandler?.setFieldValue(
              'answer',
              '',
            );
          }}
          disabled={investigationReportingAddNewQuestionHandler?.isSubmitting}
          errorMessage={
            investigationReportingAddNewQuestionHandler?.touched.questionType &&
            investigationReportingAddNewQuestionHandler?.errors.questionType
              ? investigationReportingAddNewQuestionHandler?.errors.questionType
              : ('' as any)
          }
          label="Question Type"
          placeholder="Select question type"
          otherClasses={{
            container: 'w-full ',
          }}
        />
        <Input
          name="question"
          value={investigationReportingAddNewQuestionHandler?.values.question}
          onChange={investigationReportingAddNewQuestionHandler?.handleChange}
          placeholder="Type here question..."
          type="text"
          errorMessage={
            investigationReportingAddNewQuestionHandler?.touched.question &&
            investigationReportingAddNewQuestionHandler?.errors.question
              ? investigationReportingAddNewQuestionHandler?.errors.question
              : ('' as any)
          }
          label="Question"
          onBlur={investigationReportingAddNewQuestionHandler?.handleBlur}
          disabled={investigationReportingAddNewQuestionHandler?.isSubmitting}
          otherClasses={{ container: 'w-full mt-8' }}
        />
        {investigationReportingAddNewQuestionHandler?.values?.questionType ===
        InvestigationQuestionTypeEnum?.LongAnswerText ? (
          <TextArea
            name="answer"
            value={investigationReportingAddNewQuestionHandler?.values.answer}
            onChange={investigationReportingAddNewQuestionHandler?.handleChange}
            placeholder="Type here answer..."
            errorMessage={
              investigationReportingAddNewQuestionHandler?.touched.answer &&
              investigationReportingAddNewQuestionHandler?.errors.answer
                ? investigationReportingAddNewQuestionHandler?.errors.answer
                : ('' as any)
            }
            label="Answer"
            onBlur={investigationReportingAddNewQuestionHandler?.handleBlur}
            disabled={investigationReportingAddNewQuestionHandler?.isSubmitting}
            otherClasses={{ container: 'w-full mt-8' }}
          />
        ) : investigationReportingAddNewQuestionHandler?.values
            ?.questionType === InvestigationQuestionTypeEnum?.YesNo ? (
          <Select
            list={[
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ]}
            value={investigationReportingAddNewQuestionHandler?.values.answer}
            onSelect={(value) => {
              investigationReportingAddNewQuestionHandler?.setFieldValue(
                'answer',
                value,
              );
            }}
            disabled={investigationReportingAddNewQuestionHandler?.isSubmitting}
            errorMessage={
              investigationReportingAddNewQuestionHandler?.touched.answer &&
              investigationReportingAddNewQuestionHandler?.errors.answer
                ? investigationReportingAddNewQuestionHandler?.errors.answer
                : ('' as any)
            }
            label="Answer"
            placeholder="Select answer"
            otherClasses={{
              container: 'w-full mt-8',
              listContainer: 'top-auto bottom-69',
            }}
          />
        ) : (
          <Input
            name="answer"
            value={
              investigationReportingAddNewQuestionHandler?.values.answer || ''
            }
            onChange={investigationReportingAddNewQuestionHandler?.handleChange}
            placeholder="Type here answer..."
            type="text"
            errorMessage={
              investigationReportingAddNewQuestionHandler?.touched.answer &&
              investigationReportingAddNewQuestionHandler?.errors.answer
                ? investigationReportingAddNewQuestionHandler?.errors.answer
                : ('' as any)
            }
            label="Answer"
            onBlur={investigationReportingAddNewQuestionHandler?.handleBlur}
            disabled={investigationReportingAddNewQuestionHandler?.isSubmitting}
            otherClasses={{ container: 'w-full mt-8' }}
          />
        )}

        <div className="mt-8 flex flex-col-reverse gap-15 md:grid md:grid-cols-2 md:gap-6 lg:mt-12">
          <Button
            variant="secondary"
            type="button"
            size={md}
            onClick={() => {
              investigationReportingAddNewQuestionHandler.resetForm();
              handleClose();
            }}
            disable={investigationReportingAddNewQuestionHandler?.isSubmitting}
            otherClasses="text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size={md}
            disable={
              investigationReportingAddNewQuestionHandler?.values?.question ===
                investigationReportingAddNewQuestionHandler?.initialValues
                  ?.question &&
              investigationReportingAddNewQuestionHandler?.values
                ?.questionType ===
                investigationReportingAddNewQuestionHandler?.initialValues
                  ?.questionType &&
              investigationReportingAddNewQuestionHandler?.values?.answer ===
                investigationReportingAddNewQuestionHandler?.initialValues
                  ?.answer
            }
          >
            {selectedQuestion ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InvestigationReportingAddQuestionModal;
